/**
 * Hand-offs to the person in charge (docs/ux/LAUNCH-PROPOSALS.md Wave 1 item 5; Food Code 2-101.11).
 *
 * A report, a "Told the person in charge" fix, or "Hand off the shift" goes to the person in charge ON SHIFT (not
 * the reporter), else to the managers and owners of that location. A push reaches them only if they turned reminders
 * on (push.ts; docs/NOTIFICATIONS.md), so the app still says what's true for everyone: "It's on Maria's list. Tell
 * them in person too." Each recipient gets one ShiftNotice row; the push dispatcher sends the rows where pushedAt is
 * null (words from push.ts templates, never these), then sets it.
 *
 * Pure functions: the API, the web demo and the phone demo all route the same way (T7).
 */
import { firstName } from "./actions.ts";
import type { Id, Role } from "./model.ts";

/** Someone who might receive a hand-off at one location today. `role` is their sign-in role (none: no sign-in yet). */
export interface InChargeCandidate {
  id: Id;
  name: string;
  isPersonInCharge: boolean;
  /** On shift today (core DayContext.onShift). */
  onShift: boolean;
  role?: Role;
}

export interface HandoffPerson { employeeId: Id; name: string }

export interface HandoffTarget {
  to: HandoffPerson[];
  /**
   * Why these people: the person in charge on shift, the managers/owners (no one in charge on shift), the sender
   * themself (they're the one in charge and nobody else is: a solo owner or a lone shift lead), or nobody.
   */
  via: "person_in_charge" | "manager" | "self" | "none";
  /** What the screen says after sending: "It's on Maria's list. Tell them in person too." */
  line: string;
}

const LEAD_RANK: Partial<Record<Role, number>> = { owner: 0, admin: 1, manager: 2 };

/** "Sam", "Sam and Maria", "Sam, Maria and Jo" (first names). */
export function namesLine(names: string[]): string {
  const f = names.map((n) => firstName(n));
  return f.length <= 1 ? (f[0] ?? "") : `${f.slice(0, -1).join(", ")} and ${f[f.length - 1]}`;
}

/** Whose list a hand-off lands on, as the screen names them before sending ("Maria"); undefined when nobody. */
export function handoffNames(t?: Pick<HandoffTarget, "to" | "via">): string | undefined {
  if (!t?.to.length) return undefined;
  return namesLine((t.via === "manager" ? t.to.slice(0, 1) : t.to).map((p) => p.name));
}

/**
 * "Get the person in charge" on Report's "Do this first": who to go and get, now, in person (nothing is sent by the
 * button; push isn't built). The person in charge on shift, else the manager the report goes to, else say so.
 */
export function getInChargeLine(t?: Pick<HandoffTarget, "to" | "via">): string {
  if (t?.via === "self") return SELF_GET_LINE;
  const names = handoffNames(t);
  if (!t || !names) return "Nobody is set as in charge here. Get a manager or the owner now.";
  if (t.via === "manager") return `${names} is the manager to get. Go get them now.`;
  return `${names} ${t.to.length > 1 ? "are" : "is"} in charge. Go get them now.`;
}

/** The honest line after a hand-off: whose list it's on, and to tell them in person (no push yet). */
export function handoffLine(to: Pick<HandoffPerson, "name">[]): string {
  if (!to.length) return "Nobody is set as in charge here yet. Tell the person in charge in person.";
  return `It's on ${namesLine(to.map((p) => p.name))}'s list. Tell them in person too.`;
}

/** "Get the person in charge" when that's you (persona audit 2026-09-29 #9: the owner was told "Nobody is set as in charge"). */
export const SELF_GET_LINE = "You're the person in charge here. Do these steps now. Then you decide what's next.";
/** After sending a report when you're the one in charge and nobody else is. */
export const SELF_REPORT_LINE = "You're the person in charge, so this one is yours. It's on Today's open reports.";
/**
 * After "I'm sick or was exposed" when you're the one in charge: nobody else will tell you, so the app does. Stop now;
 * the check-in decides exclude or restrict (Food Code 2-201.12), as it does for everyone (checkin.ts CHECKIN_ACTIONS).
 * The action, never a symptom (T4).
 */
export const SELF_SICK_LINE = "You're the person in charge. Stop food work now. Do today's check-in next. It tells you if you must stay off food or go home (Food Code 2-201.12).";

/** The line after sending a report of `incidentType`: the hand-off line, or the direct instruction when it's yours. */
export function reportSentLine(t: Pick<HandoffTarget, "via" | "line">, incidentType: string): string {
  if (t.via !== "self") return t.line;
  return incidentType === "illness" ? SELF_SICK_LINE : SELF_REPORT_LINE;
}

/**
 * Who a hand-off from `fromEmployeeId` reaches: every person in charge on shift except the sender; if there is none,
 * the managers, admins and owners at this location (on shift first, then owner, admin, manager), never the sender.
 */
export function handoffTarget(people: InChargeCandidate[], fromEmployeeId?: Id, fromRole?: Role): HandoffTarget {
  const others = people.filter((p) => p.id !== fromEmployeeId);
  const pics = others.filter((p) => p.onShift && p.isPersonInCharge);
  if (pics.length) {
    const to = pics.map((p) => ({ employeeId: p.id, name: p.name }));
    return { to, via: "person_in_charge", line: handoffLine(to) };
  }
  const leads = others
    .filter((p) => p.role !== undefined && LEAD_RANK[p.role] !== undefined)
    .sort((a, b) => Number(b.onShift) - Number(a.onShift) || LEAD_RANK[a.role!]! - LEAD_RANK[b.role!]! || a.name.localeCompare(b.name));
  if (leads.length) {
    const to = leads.map((p) => ({ employeeId: p.id, name: p.name }));
    // The line names the first (the one most likely here); the others have it on their list too.
    return { to, via: "manager", line: handoffLine(to.slice(0, 1)) };
  }
  // Nobody else to hand it to. If the sender is the one in charge (flagged in charge, as Team shows it, or an owner,
  // admin or manager, who handoffs fall back to), say so: "You're the person in charge", never "Nobody is in charge".
  // `fromRole` covers a sign-in with no employee record (an owner who never added themself to the team).
  const me = fromEmployeeId ? people.find((p) => p.id === fromEmployeeId) : undefined;
  const role = me?.role ?? fromRole;
  if (me?.isPersonInCharge || (role !== undefined && LEAD_RANK[role] !== undefined)) return { to: [], via: "self", line: SELF_REPORT_LINE };
  return { to: [], via: "none", line: handoffLine([]) };
}

/**
 * One pending notice for one person. Written when a hand-off happens; shown on the recipient's For You.
 * The push dispatcher sends the rows with no `pushedAt` and stamps it; `seenAt` is for a future "I'm on it".
 */
export interface ShiftNotice {
  id: Id;
  orgId: Id;
  locationId: Id;
  toEmployeeId: Id;
  fromEmployeeId?: Id;
  /**
   * nag: a manager's Nag (nag.ts), refId = the item ref ("check:cold-4h@11:00"). nudge: a gentle quick-log question
   * (quicklog.ts, "Has anyone checked the kitchen trash?"), refId = "quick:<key>"; shown by Quick log, not as a hand-off.
   * health: a health alert (locationhealth.ts healthAlerts), refId = "health:<item>@<found>"; to the person in charge.
   * assign: a task given to this person (delegate.ts), refId = "check:<key>" like a Nag so the card opens the task.
   * order: items added to the order list (orderalert.ts), to managers; refId = the alert's batch id; one row per manager, updated in place.
   */
  kind: "report" | "shift" | "nag" | "nudge" | "health" | "assign" | "order";
  /** The incident id for a report. */
  refId?: Id;
  title: string;
  body: string;
  createdAt: string;
  pushedAt?: string;
  seenAt?: string;
  /** A "needs help" request from a step (helprequest.ts): refId is `help:<id>`; this keeps where, who, and whether someone is on the way. */
  help?: import("./helprequest.ts").HelpData;
}

/** A notice as the recipient's screen reads it (GET /today me.handoffs). */
export type ShiftNoticeView = Pick<ShiftNotice, "id" | "kind" | "title" | "body" | "createdAt"> & {
  refId?: Id;
  /** kind "order": the alert's lines, with who can be thanked (orderalert.ts). */
  order?: import("./orderalert.ts").OrderAlertView;
};

export const noticeView = (n: ShiftNotice): ShiftNoticeView => ({ id: n.id, kind: n.kind, title: n.title, body: n.body, createdAt: n.createdAt, ...(n.refId ? { refId: n.refId } : {}) });

/** The words on a report notice. A sick report says only that they shouldn't do food work (T4: never the symptom). */
export function reportNoticeText(o: { fromName?: string; incidentType: string; incidentTitle: string; details?: string }): { title: string; body: string } {
  const who = o.fromName ? firstName(o.fromName) : "Someone";
  if (o.incidentType === "illness") return { title: `${who} shouldn't do food work today`, body: "They told you privately. You decide their work today (Food Code 2-201.11)." };
  return { title: `${who}: ${o.incidentTitle}`, body: o.details?.trim() ? o.details.trim().slice(0, 300) : "It's on Today's open reports until it's handled." };
}

/** The words on a "Hand off the shift" notice. */
export function shiftNoticeText(o: { fromName?: string; checksOpen: number; problemsOpen: number; note?: string }): { title: string; body: string } {
  const who = o.fromName ? firstName(o.fromName) : "Someone";
  const parts = [o.checksOpen ? `${o.checksOpen} ${o.checksOpen === 1 ? "check" : "checks"}` : "", o.problemsOpen ? `${o.problemsOpen} ${o.problemsOpen === 1 ? "problem" : "problems"}` : ""].filter(Boolean);
  const open = parts.length ? `Still open: ${parts.join(" and ")}. The list is on Today.` : "Nothing left open. A clean close.";
  return { title: `${who} handed you the shift`, body: o.note?.trim() ? `${open} Note: ${o.note.trim().slice(0, 300)}` : open };
}
